import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoSrc from "@/assets/arqera-logo-optimized.png";
import { analytics } from "@/lib/analytics";

interface NavigationProps {
  activeSection: string;
}

interface NavLink {
  label: string;
  href: string;
  isRoute?: boolean;
}

export default function Navigation({ activeSection }: NavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [clickedLink, setClickedLink] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks: NavLink[] = [
    { label: "Services", href: "#services" },
    { label: "Results", href: "#results" },
    { label: "Crystal Ball", href: "/quality-crystal-ball", isRoute: true },
    { label: "Free AI Audit", href: "/ai-transformation-audit", isRoute: true },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
  ];

  const scrollToSection = (href: string, isRoute?: boolean) => {
    setClickedLink(href);
    
    // Track navigation click
    const linkLabel = navLinks.find(link => link.href === href)?.label || href;
    analytics.clickNavLink(linkLabel);
    
    if (isRoute) {
      window.location.href = href;
      setIsMobileMenuOpen(false);
      setTimeout(() => setClickedLink(null), 600);
      return;
    }
    
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      analytics.scrollToSection(href.slice(1));
      setIsMobileMenuOpen(false);
      
      // Reset clicked state after animation
      setTimeout(() => setClickedLink(null), 600);
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-deep-navy/90 backdrop-blur-md border-b-2 border-cyan/40"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo & Brand */}
          <a 
            href="#top" 
            className="flex items-center gap-3 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded-md"
            aria-label="ArqeraAI Home"
          >
            <img src={logoSrc} alt="ArqeraAI Logo" className="h-10 w-10" />
            <span className="text-base font-bold tracking-wider text-foreground uppercase">
              ARQERA.AI
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href, link.isRoute)}
                className={`text-sm font-medium transition-all duration-300 hover:text-cyan px-2 py-1 border-b-2 relative focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded
                  ${activeSection === link.href.slice(1)
                    ? "text-cyan border-cyan"
                    : "text-foreground border-transparent"
                  }
                  ${clickedLink === link.href ? "animate-scale-in" : ""}
                  hover:scale-105 hover:shadow-lg hover:shadow-cyan/20
                `}
                aria-label={`Navigate to ${link.label}`}
                aria-current={activeSection === link.href.slice(1) ? "page" : undefined}
              >
                <span className="relative">
                  {link.label}
                  {link.label === "Free AI Audit" && (
                    <span className="absolute -top-2 -right-8 bg-cyan text-deep-navy text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider animate-pulse">
                      Free
                    </span>
                  )}
                </span>
                {clickedLink === link.href && (
                  <span className="absolute inset-0 rounded-md bg-cyan/10 animate-pulse" />
                )}
              </button>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              asChild
              className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy"
            >
              <a
                href="https://calendly.com/arqeraai-strategy-call/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickScheduleCall('nav_cta')}
                aria-label="Schedule a strategy call with ArqeraAI"
              >
                Schedule Call
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-cyan hover:text-cyan/80 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded p-1"
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu"
          className="fixed inset-0 z-40 bg-deep-navy/95 backdrop-blur-md md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <div className="flex flex-col items-center justify-center h-full gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href, link.isRoute)}
                className={`text-2xl font-semibold transition-all duration-300 hover:text-cyan hover:scale-110 relative focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy rounded px-4 py-2
                  ${activeSection === link.href.slice(1) ? "text-cyan" : "text-foreground"}
                  ${clickedLink === link.href ? "animate-scale-in" : ""}
                `}
                aria-label={`Navigate to ${link.label}`}
              >
                <span className="relative">
                  {link.label}
                  {link.label === "Free AI Audit" && (
                    <span className="absolute -top-4 -right-12 bg-cyan text-deep-navy text-xs font-bold px-2 py-1 rounded uppercase tracking-wider animate-pulse">
                      Free
                    </span>
                  )}
                </span>
                {clickedLink === link.href && (
                  <span className="absolute inset-0 rounded-md bg-cyan/10 animate-pulse" />
                )}
              </button>
            ))}
            <Button
              asChild
              className="bg-cyan text-white hover:bg-cyan/90 w-64 py-6 text-lg focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-2 focus:ring-offset-deep-navy"
            >
              <a
                href="https://calendly.com/arqeraai-strategy-call/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.clickScheduleCall('mobile_nav_cta')}
                aria-label="Schedule a strategy call with ArqeraAI"
              >
                Schedule Call
              </a>
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
